import { MessageDialog, useDialogManager } from '../../../../../../widgets/dialog';
import { BasicButton } from '../../../../../../widgets/buttons';
import { FormControl } from '../../../../../../widgets/formControl';
import { Input } from '../../../../../../widgets/input';
import { TextArea } from '../../../../../../widgets/textArea';
import { Checkbox } from '../../../../../../widgets/checkbox';
import { Tab, TabList, TabPanel } from '../../../../../../widgets/ztabs';
import { ZTooltip } from '../../../../../../widgets/ztooltip';
import React, { FC } from 'react';
import StoreIcon from 'src/shared/material_icons/action/store.svg';
import { ComposerSlot, ComposerSlotGlyph } from '../composerSlots';
import { PropertiesPanelResourceProperty } from '../../scene-properties-panel';
import { useProductConfigurationState } from 'src/components/pages/productConfiguration/state';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import { T } from 'src/shared/helpers';
import DeleteIcon from 'src/shared/material_icons/action/delete.svg';
import PlayIcon from 'src/shared/material_icons/av/play_arrow.svg';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import { CollapseAllIcon, ExpandAllIcon, SingleBranchIcon } from 'src/components/widgets/icons/scene';
import OptionIcon from 'src/shared/material_icons/image/palette.svg';
import styled from 'styled-components';
import { ComposerActionDraftCard, ComposerActionTreeElement } from '../../../configurator-composer/composer/components/composerActionTreeElement';
import { carriedFrom, reorderIds, startPointerReorder } from '../../pointerReorder';
import Composer3DPreviewCapture from '../../../configurator-composer/composer/components/composer3DPreviewCapture';
import ComposerUploadComponent from '../../../configurator-composer/composer/components/composerUploadComponent';
import useComposerStore, { ComposerOptionFace } from '../../../configurator-composer/composer/composerStore';
import { ComposerRevealButton } from '../composerReveal';
import { ComposerValidationErrorCode, hasComposerValidationError } from '../../../configurator-composer/composer/helpers/composerAttributeValidation';
import ComposerPlatformProductDialog from '../../../configurator-composer/composer/dialogs/composerPlatformProductDialog';
import ComposerActionEditor, { createComposerAction } from './composerActionEditor';
import { sortByDisplay } from '../../../configurator-composer/composer/helpers/composerHelpers';
import { createComposerLocalCode, isComposerStoreCode } from '../../../configurator-composer/composer/helpers/composerLocalCode';
import { ComposerAttributeNodeAdapter } from '../../../configurator-composer/composer/helpers/composerNodesAdapter';
import {
    ComposerPlatformProduct,
    ComposerPlatformVariant,
    createComposerPlatformVariantCode,
    loadComposerPlatformVariants,
    parseComposerPlatformVariantCode
} from '../../../configurator-composer/composer/helpers/composerPlatformPricing';
import { applyComposerOptionImage, createComposerOptionImageReference } from '../../../configurator-composer/composer/helpers/composerOptionImage';
import type { TreeNodeHook } from '../../tree';
import {
    ComposerAddButton,
    ComposerAddCard,
    ComposerEmptyList,
    ComposerFieldNote,
    ComposerDetailCard,
    ComposerDetailLabel,
    ComposerDetailsStack,
    ComposerDetailValue,
    ComposerEmptyState,
    ComposerFormStack,
    ComposerIconButton,
    ComposerKindBadge,
    ComposerList,
    ComposerPanel,
    ComposerScrollBody,
    ComposerSection,
    ComposerSettingsBody,
    ComposerSubjectContent,
    ComposerSubjectHeader,
    ComposerSubjectMark,
    ComposerSubjectMeta,
    ComposerSubjectNameInput,
    ComposerTabCount,
    ComposerTabs,
    ComposerToolbar,
    ComposerToolbarSpacer,
    ComposerUnsupportedNote
} from '../composerWorkspace';
import ComposerLinksEditor from './composerLinksEditor';
import ComposerRetargetPanel from './composerRetargetPanel';
import {
    ComposerAttributeNode,
    ComposerAttributeOptionNode,
    ComposerEcommercePlatform,
    ComposerExtensionFieldNode,
    ComposerOptionActionNode,
    ComposerOptionActionsType,
    ComposerPricingNode
} from './composerAttributesModels';

const TabBody = styled.div`
    min-height: 0;
    flex: 1;
    overflow: auto;
    overscroll-behavior: contain;
    padding: 8px;
`;

const PropertyEditorActions = styled.div`
    display: flex;
    gap: 8px;
`;

const PriceModeOptions = styled.div`
    display: grid;
    gap: 6px;
`;

const PriceModeLabel = styled.label`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 9px 10px;
    border: 1px solid var(--composer-line, #dce3eb);
    border-radius: 7px;
    cursor: pointer;

    input {
        margin-top: 2px;
        accent-color: var(--composer-accent, #e8650a);
    }

    span {
        display: grid;
        gap: 2px;
    }

    small {
        color: var(--composer-muted, #5c6b7a);
        font-size: 11px;
    }
`;

const ComposerWarningNote = styled.div`
    padding: 7px 9px;
    border: 1px solid var(--composer-warn-line-strong, #f2c98d);
    border-radius: 6px;
    color: var(--composer-warn-ink, #874b0b);
    background: var(--composer-warn-surface, #fff8ed);
    font-size: 11px;
    line-height: 1.35;
`;

const PropertiesContent: FC<{ selectedOption: ComposerAttributeOptionNode; onUpdate: (option: ComposerAttributeOptionNode) => void }> = ({
    selectedOption,
    onUpdate
}) => {
    const globalFields = useComposerStore((store) => store.extensionFieldDefinitions);
    const [isEditing, setIsEditing] = React.useState(false);
    const [editValues, setEditValues] = React.useState<Record<string, string>>({});
    const fields = selectedOption.extensionFields || [];

    const startEditing = () => {
        setEditValues(Object.fromEntries(fields.map((field) => [String(field.extensionFieldID), field.value])));
        setIsEditing(true);
    };

    const save = () => {
        const extensionFields: ComposerExtensionFieldNode[] = globalFields
            .filter((field) => editValues[String(field.fieldID)] !== undefined && editValues[String(field.fieldID)] !== '')
            .map((field) => ({
                extensionFieldID: Number(field.fieldID),
                name: field.name || '',
                label: field.label || '',
                value: editValues[String(field.fieldID)] || '',
                dataTypeID: field.dataTypeID,
                operationTypeID: field.operationTypeID,
                formatString: field.formatString || ''
            }));
        onUpdate({ ...selectedOption, extensionFields });
        setIsEditing(false);
    };

    if (isEditing) {
        return (
            <ComposerDetailsStack>
                {globalFields.length === 0 ? (
                    <ComposerUnsupportedNote>{T._('No extension fields are configured in Additional Properties.', 'Admin')}</ComposerUnsupportedNote>
                ) : (
                    globalFields.map((field) => (
                        <FormControl key={String(field.fieldID)} label={field.label || field.name || ''}>
                            <Input
                                type='text'
                                placeholder={field.dataTypeName || ''}
                                value={editValues[String(field.fieldID)] || ''}
                                onChange={(event) => setEditValues((current) => ({ ...current, [String(field.fieldID)]: event.target.value }))}
                            />
                        </FormControl>
                    ))
                )}
                <PropertyEditorActions>
                    <BasicButton onClick={save}>{T._('Save', 'Admin')}</BasicButton>
                    <BasicButton onClick={() => setIsEditing(false)}>{T._('Cancel', 'Admin')}</BasicButton>
                </PropertyEditorActions>
            </ComposerDetailsStack>
        );
    }

    return (
        <ComposerDetailsStack>
            {fields.length === 0 ? (
                <ComposerEmptyState>{T._('No additional properties are set for this option.', 'Admin')}</ComposerEmptyState>
            ) : (
                fields.map((field) => (
                    <ComposerDetailCard key={field.extensionFieldID}>
                        <ComposerDetailLabel>{field.label || field.name}</ComposerDetailLabel>
                        <ComposerDetailValue>{field.value}</ComposerDetailValue>
                    </ComposerDetailCard>
                ))
            )}
            <BasicButton onClick={startEditing} disabled={globalFields.length === 0}>
                {fields.length === 0 ? T._('Add properties', 'Admin') : T._('Edit properties', 'Admin')}
            </BasicButton>
        </ComposerDetailsStack>
    );
};

const PriceContent: FC<{
    selectedAttribute: ComposerAttributeNode;
    selectedOption: ComposerAttributeOptionNode;
    ecommercePlatform: ComposerEcommercePlatform;
    onUpdateAttribute: (attribute: ComposerAttributeNode) => void;
    onUpdateOption: (option: ComposerAttributeOptionNode) => void;
}> = ({ selectedAttribute, selectedOption, ecommercePlatform, onUpdateAttribute, onUpdateOption }) => {
    const { state: productState } = useProductConfigurationState();
    const { showDialog, closeDialog } = useDialogManager();
    const optionPricing = (selectedAttribute.pricings || []).find((pricing) => pricing.selectedOptionGuid === selectedOption.id);
    const [price, setPrice] = React.useState(optionPricing?.price?.toString() || '');
    const [chargeName, setChargeName] = React.useState(optionPricing?.name || selectedOption.name);
    const linkedVariant = React.useMemo(() => parseComposerPlatformVariantCode(selectedOption.code), [selectedOption.code]);
    const isPlatformCharge = ecommercePlatform === ComposerEcommercePlatform.Shopify || ecommercePlatform === ComposerEcommercePlatform.BigCommerce;
    const [adjustPrice, setAdjustPrice] = React.useState(Boolean(optionPricing || linkedVariant));
    const [priceMode, setPriceMode] = React.useState<'charge' | 'variant'>(linkedVariant ? 'variant' : 'charge');
    const [selectedProduct, setSelectedProduct] = React.useState<ComposerPlatformProduct | null>(
        linkedVariant
            ? {
                  code: linkedVariant.product.code,
                  name: linkedVariant.product.code,
                  metadata: linkedVariant.product.metadata
              }
            : null
    );
    const [variants, setVariants] = React.useState<ComposerPlatformVariant[]>(linkedVariant ? [linkedVariant.variant] : []);
    const [variantsLoading, setVariantsLoading] = React.useState(false);
    const [variantsError, setVariantsError] = React.useState<string | null>(null);

    React.useEffect(() => {
        const pricing = (selectedAttribute.pricings || []).find((candidate) => candidate.selectedOptionGuid === selectedOption.id);
        setPrice(pricing?.price?.toString() || '');
        setChargeName(pricing?.name || selectedOption.name);
        const currentVariant = parseComposerPlatformVariantCode(selectedOption.code);
        setAdjustPrice(Boolean(pricing || currentVariant));
        setPriceMode(currentVariant ? 'variant' : 'charge');
        setSelectedProduct((currentProduct) =>
            currentVariant
                ? currentProduct?.code === currentVariant.product.code
                    ? currentProduct
                    : { code: currentVariant.product.code, name: currentVariant.product.code, metadata: currentVariant.product.metadata }
                : null
        );
        setVariants((currentVariants) =>
            currentVariant ? (currentVariants.some((variant) => variant.code === currentVariant.variant.code) ? currentVariants : [currentVariant.variant]) : []
        );
        setVariantsError(null);
        // A mode change clears the old price before a variant is selected. Syncing on
        // every intermediate store update would consequently turn Adjust price off.
        // The selected option is the boundary at which this local price UI must reset.
    }, [selectedOption.id]);

    const removeOptionPricing = () => {
        onUpdateAttribute({
            ...selectedAttribute,
            pricings: (selectedAttribute.pricings || []).filter((pricing) => pricing.selectedOptionGuid !== selectedOption.id)
        });
    };

    const upsertOptionPricing = (value: string, name = chargeName) => {
        const currentPricings = [...(selectedAttribute.pricings || [])];
        const index = currentPricings.findIndex((pricing) => pricing.selectedOptionGuid === selectedOption.id);
        const pricing: ComposerPricingNode = {
            attributePricingID: optionPricing?.attributePricingID || 0,
            selectedOptionID: selectedOption.idNumeric || 0,
            selectedOptionGuid: selectedOption.id,
            selectedValue: '',
            price: Number.parseFloat(value) || 0,
            pricingID: optionPricing?.pricingID || 0,
            name: name.trim() || selectedOption.name
        };
        index >= 0 ? (currentPricings[index] = pricing) : currentPricings.push(pricing);
        onUpdateAttribute({ ...selectedAttribute, pricings: currentPricings });
    };

    const updatePrice = (value: string) => {
        setPrice(value);
        upsertOptionPricing(value);
    };

    const selectProduct = async (product: ComposerPlatformProduct) => {
        setSelectedProduct(product);
        setVariants([]);
        setVariantsLoading(true);
        setVariantsError(null);
        try {
            const loadedVariants = await loadComposerPlatformVariants(product.code);
            setVariants(loadedVariants);
            if (loadedVariants.length === 1) {
                onUpdateOption({
                    ...selectedOption,
                    platformAttribute: undefined,
                    code: createComposerPlatformVariantCode(product, loadedVariants[0])
                });
            } else {
                onUpdateOption({ ...selectedOption, platformAttribute: undefined, code: '' });
            }
        } catch (error) {
            setVariantsError(T._('Store variants could not be loaded. Choose the product again or retry later.', 'Admin'));
        } finally {
            setVariantsLoading(false);
        }
    };

    const setPlatformPricingEnabled = (enabled: boolean) => {
        setAdjustPrice(enabled);
        setVariantsError(null);
        if (!enabled) {
            removeOptionPricing();
            setSelectedProduct(null);
            setVariants([]);
            onUpdateOption({
                ...selectedOption,
                platformAttribute: undefined,
                code: selectedOption.code.trim() && !linkedVariant ? selectedOption.code : createComposerLocalCode(selectedOption.name, 'option')
            });
            return;
        }

        setPriceMode('charge');
        setPrice(optionPricing?.price?.toString() || '');
        setChargeName(optionPricing?.name || selectedOption.name);
        upsertOptionPricing(optionPricing?.price?.toString() || '', optionPricing?.name || selectedOption.name);
        onUpdateOption({ ...selectedOption, platformAttribute: undefined, code: '' });
    };

    const setPlatformPriceMode = (mode: 'charge' | 'variant') => {
        setPriceMode(mode);
        setVariantsError(null);
        if (mode === 'charge') {
            setSelectedProduct(null);
            setVariants([]);
            upsertOptionPricing(price, chargeName);
            onUpdateOption({ ...selectedOption, platformAttribute: undefined, code: '' });
        } else {
            removeOptionPricing();
            onUpdateOption({ ...selectedOption, platformAttribute: undefined, code: '' });
        }
    };

    // IL PRODOTTO È UNO SLOT COME LA SUA VARIANTE (utente, 2026-10-15): l'elenco dei prodotti dello store è
    // lungo e si cerca sul server, quindi la riga apre la finestra di sempre invece di una tendina.
    const pickProduct = () =>
        showDialog(
            'composerPlatformProduct',
            <ComposerPlatformProductDialog
                salesChannelID={productState.salesChannelID}
                forbiddenProductCode={productState.product.code}
                onClose={() => closeDialog('composerPlatformProduct')}
                onSelect={(product) => void selectProduct(product)}
            />
        );

    if (isPlatformCharge) {
        return (
            <ComposerDetailsStack>
                <Checkbox label={T._('Adjust price', 'Admin')} checked={adjustPrice} onChange={setPlatformPricingEnabled} />
                {adjustPrice && (
                    <>
                        <PriceModeOptions role='radiogroup' aria-label={T._('Price adjustment type', 'Admin')}>
                            <PriceModeLabel>
                                <input type='radio' checked={priceMode === 'charge'} onChange={() => setPlatformPriceMode('charge')} />
                                <span>
                                    <strong>{T._('Create a charge', 'Admin')}</strong>
                                    <small>{T._('Creates an active product that remains hidden in your store.', 'Admin')}</small>
                                </span>
                            </PriceModeLabel>
                            <PriceModeLabel>
                                <input type='radio' checked={priceMode === 'variant'} onChange={() => setPlatformPriceMode('variant')} />
                                <span>
                                    <strong>{T._('Use a variant from another (existing) product in your shop', 'Admin')}</strong>
                                    <small>{T._('Use the price of a variant that already exists in the store.', 'Admin')}</small>
                                </span>
                            </PriceModeLabel>
                        </PriceModeOptions>
                        {priceMode === 'charge' ? (
                            <>
                                <FormControl label={T._('Charge name', 'Admin')}>
                                    <Input
                                        value={chargeName}
                                        placeholder={selectedOption.name}
                                        onChange={(event) => {
                                            setChargeName(event.target.value);
                                            upsertOptionPricing(price, event.target.value);
                                        }}
                                    />
                                </FormControl>
                                <FormControl label={T._('Price', 'Admin')}>
                                    <Input type='number' placeholder='0.00' value={price} onChange={(event) => updatePrice(event.target.value)} />
                                </FormControl>
                            </>
                        ) : (
                            <>
                                <FormControl label={T._('Store product', 'Admin')}>
                                    <div data-composer-wide>
                                        <PropertiesPanelResourceProperty
                                            id={`composer-option-${selectedOption.id}-product`}
                                            preview={
                                                <ComposerSlotGlyph>
                                                    {selectedProduct?.thumbnail ? <img src={selectedProduct.thumbnail} alt='' /> : <StoreIcon />}
                                                </ComposerSlotGlyph>
                                            }
                                            name={selectedProduct?.name}
                                            caption={selectedProduct?.code}
                                            emptyLabel={T._('No product selected', 'Admin')}
                                            emptyCaption={T._('Choose a product', 'Admin')}
                                            onPick={pickProduct}
                                            onReplace={pickProduct}
                                        />
                                    </div>
                                </FormControl>
                                {selectedProduct && (
                                    <FormControl label={T._('Store variant', 'Admin')}>
                                        <ComposerSlot
                                            id={`composer-option-${selectedOption.id}-variant`}
                                            value={linkedVariant?.variant.code}
                                            options={variants.map((variant) => ({
                                                value: variant.code,
                                                label: variant.name || variant.sku || variant.code,
                                                caption: variant.sku && variant.name ? variant.sku : undefined
                                            }))}
                                            glyph={<StoreIcon />}
                                            caption={T._('Store variant', 'Admin')}
                                            emptyLabel={variantsLoading ? T._('Loading variants…', 'Admin') : T._('Choose a variant', 'Admin')}
                                            searchPlaceholder={T._('Search variants…', 'Admin')}
                                            noOptionsLabel={T._('This product has no variants.', 'Admin')}
                                            disabled={variantsLoading}
                                            clearable
                                            onChange={(code) => {
                                                const variant = variants.find((candidate) => candidate.code === code);
                                                onUpdateOption({
                                                    ...selectedOption,
                                                    platformAttribute: undefined,
                                                    code: variant ? createComposerPlatformVariantCode(selectedProduct, variant) : ''
                                                });
                                            }}
                                        />
                                    </FormControl>
                                )}
                                {variantsError && <ComposerWarningNote>{variantsError}</ComposerWarningNote>}
                            </>
                        )}
                    </>
                )}
            </ComposerDetailsStack>
        );
    }

    return (
        <ComposerDetailsStack>
            <FormControl label={T._('Option price', 'Admin')} tooltipLabelHtml={T._('Set the price adjustment for this option.', 'Admin')}>
                <Input type='number' placeholder='0.00' value={price} onChange={(event) => updatePrice(event.target.value)} />
            </FormControl>
            <ComposerUnsupportedNote>
                {T._('The current product API stores one price adjustment per option, not a list of prices.', 'Admin')}
            </ComposerUnsupportedNote>
        </ComposerDetailsStack>
    );
};

const ComposerActions: FC<{
    selectedAttribute: ComposerAttributeNode;
    selectedOption?: ComposerAttributeOptionNode;
    actions: ComposerOptionActionNode[];
    multiple: boolean;
    state: TreeNodeHook<ComposerOptionActionNode>;
    materials?: BABYLON.Material[];
    meshes?: BABYLON.AbstractMesh[];
    cameras?: BABYLON.Camera[];
}> = ({ selectedAttribute, selectedOption, actions, multiple, state, materials = [], meshes = [], cameras = [] }) => {
    const { state: productState } = useProductConfigurationState();
    const [sceneEditor] = usePreview3DStore((store) => [store.sceneEditor]);
    const [
        attributes,
        updateAction,
        removeAction,
        updateOption,
        updateAttribute,
        selectActions,
        ecommerceSettings,
        optionFace,
        setOptionFace,
        validateOptions,
        merchantProductOptions,
        printTypes
    ] = useComposerStore((store) => [
        store.attributes,
        store.updateAction,
        store.removeAction,
        store.updateOption,
        store.updateAttribute,
        store.selectActions,
        store.ecommerceSettings,
        store.optionFace,
        store.setOptionFace,
        store.validateOptions,
        store.merchantProductOptions,
        store.printTypes
    ]);
    const { showDialog, closeDialog } = useDialogManager();
    const { selectedIds, filteredData, addNode, updateNode, findNodeById, removeNodes, setSelectedIds } = state;

    // APERTA E SCELTA SONO DUE COSE (utente, 2026-10-15; prototipo, Rules): una card aperta mostra il suo
    // editor, una card scelta porta il filo arancio ed entra nel mucchio su cui agisce il cestino. Prima
    // erano la stessa: si apriva soltanto la card scelta da sola, e scegliere più card le chiudeva tutte.
    // «One card at a time» acceso — com'era prima, quindi di partenza — aprirne una chiude le altre.
    const [openIds, setOpenIds] = React.useState<string[]>([]);
    const [singleCard, setSingleCard] = React.useState(true);
    // LA CARD CHE NASCE NON È ANCORA UN'AZIONE: sta qui finché non ha un tipo, e solo allora entra nello store.
    const [drafting, setDrafting] = React.useState(false);

    React.useEffect(() => {
        setSelectedIds([]);
        selectActions(null);
        setOpenIds([]);
        setDrafting(false);
    }, [selectedOption?.id]);

    const openCard = (id: string) => setOpenIds((current) => (singleCard ? [id] : current.includes(id) ? current : [...current, id]));
    const closeCard = (id: string) => setOpenIds((current) => current.filter((candidate) => candidate !== id));
    const clearSelection = () => {
        setSelectedIds([]);
        selectActions(null);
    };

    const addAction = () => {
        if (!selectedOption) return;
        setDrafting(true);
        setOptionFace('actions');
    };

    const createAction = (type: ComposerOptionActionsType) => {
        if (!selectedOption) return;
        const action = createComposerAction(selectedAttribute.id, selectedOption.id, type, actions.length);
        addNode(action);
        updateAction(action);
        setDrafting(false);
        setSelectedIds([action.id]);
        selectActions([action]);
        openCard(action.id);
    };

    const playAction = (action: ComposerOptionActionNode) => {
        sceneEditor?.executeActionsAsync(selectedAttribute.idNumeric, null, [ComposerAttributeNodeAdapter.fromActionNode(action)]);
    };

    const removeActions = (actionsToRemove: ComposerOptionActionNode[]) => {
        if (actionsToRemove.length === 0) return;
        showDialog(
            'confirmDialog',
            <MessageDialog
                title={T._('Confirm Removal', 'Admin')}
                message={T._('Are you sure you want to remove the selected action(s)?', 'Admin')}
                buttons={[
                    {
                        label: T._('Remove', 'Admin'),
                        destructive: true,
                        onClick: () => {
                            closeDialog('confirmDialog');
                            const ids = actionsToRemove.map((action) => action.id);
                            removeNodes(ids);
                            actionsToRemove.forEach((action) => removeAction(action.attributeID, action.optionID, action.id));
                            setOpenIds((current) => current.filter((id) => !ids.includes(id)));
                            setSelectedIds([]);
                            selectActions(null);
                        }
                    }
                ]}
            />
        );
    };

    const selectAction = (event: React.MouseEvent, action: ComposerOptionActionNode) => {
        setOptionFace('actions');
        if (event.shiftKey && selectedIds.length > 0) {
            const visibleActions = [...filteredData].sort((left, right) => sortByDisplay(filteredData, left, right));
            const start = visibleActions.findIndex((candidate) => candidate.id === selectedIds[selectedIds.length - 1]);
            const end = visibleActions.findIndex((candidate) => candidate.id === action.id);
            if (start < 0 || end < 0) return;
            const range = visibleActions.slice(Math.min(start, end), Math.max(start, end) + 1);
            setSelectedIds(range.map((candidate) => candidate.id));
            selectActions(range);
            return;
        }
        if (event.ctrlKey || event.metaKey) {
            const nextIds = selectedIds.includes(action.id) ? selectedIds.filter((id) => id !== action.id) : [...selectedIds, action.id];
            setSelectedIds(nextIds);
            selectActions(nextIds.map((id) => findNodeById(id)).filter(Boolean) as ComposerOptionActionNode[]);
            return;
        }
        // Il clic semplice sceglie solo questa card e la apre o la chiude; ⇧ e ⌘ toccano la scelta e basta.
        setSelectedIds([action.id]);
        selectActions([action]);
        if (openIds.includes(action.id)) closeCard(action.id);
        else openCard(action.id);
    };

    // OGNI RIGA DELLA CARD SCRIVE SUBITO (utente, 2026-10-15): niente Save. Lo store rivalida l'azione, e la
    // card riprende la versione rivalidata — con i suoi errori — come fa l'occhio.
    const changeAction = (nextAction: ComposerOptionActionNode) => {
        updateAction(nextAction);
        const validatedAction =
            useComposerStore
                .getState()
                .attributes.find((attribute) => attribute.id === nextAction.attributeID)
                ?.options.find((option) => option.id === nextAction.optionID)
                ?.actions.find((candidate) => candidate.id === nextAction.id) ?? nextAction;
        updateNode(validatedAction.id, validatedAction);
    };

    const moveAction = (action: ComposerOptionActionNode, direction: 'up' | 'down') => {
        const siblings = [...actions].sort((left, right) => sortByDisplay(actions, left, right));
        const index = siblings.findIndex((candidate) => candidate.id === action.id);
        const swapIndex = direction === 'up' ? index - 1 : index + 1;
        if (swapIndex < 0 || swapIndex >= siblings.length) return;
        const swap = siblings[swapIndex];
        updateNode(action.id, { ...action, displayOrder: swap.displayOrder });
        updateNode(swap.id, { ...swap, displayOrder: action.displayOrder });
        updateAction({ ...action, displayOrder: swap.displayOrder });
        updateAction({ ...swap, displayOrder: action.displayOrder });
    };

    // IL TRASCINAMENTO DELLE CARD (pointerReorder; utente, 2026-10-15): si sposta la card intera, e una
    // card scelta porta con sé tutte le card scelte, nel loro ordine.
    const startActionDrag = (event: React.PointerEvent<HTMLButtonElement>, dragged: ComposerOptionActionNode) => {
        const siblings = [...actions].sort((left, right) => sortByDisplay(actions, left, right));
        const ordered = siblings.map((action) => action.id);
        const carriedIds = carriedFrom(ordered, dragged.id, selectedIds);
        startPointerReorder(event, {
            draggedId: dragged.id,
            carriedIds,
            canDrop: (targetId, position) => position !== 'into' && ordered.includes(targetId) && !carriedIds.includes(targetId),
            onDrop: (targetId, position) => {
                if (position === 'into') return;
                reorderIds(ordered, carriedIds, targetId, position).forEach((id, displayOrder) => {
                    const action = siblings.find((candidate) => candidate.id === id);
                    if (!action || action.displayOrder === displayOrder) return;
                    updateNode(action.id, { ...action, displayOrder });
                    updateAction({ ...action, displayOrder });
                });
            }
        });
    };

    const toggleActionEnabled = (action: ComposerOptionActionNode) => {
        const currentAction =
            useComposerStore
                .getState()
                .attributes.find((attribute) => attribute.id === action.attributeID)
                ?.options.find((option) => option.id === action.optionID)
                ?.actions.find((candidate) => candidate.id === action.id) ?? action;
        const updatedAction = { ...currentAction, enabled: currentAction.enabled === false };
        updateAction(updatedAction);
        const validatedAction =
            useComposerStore
                .getState()
                .attributes.find((attribute) => attribute.id === updatedAction.attributeID)
                ?.options.find((option) => option.id === updatedAction.optionID)
                ?.actions.find((candidate) => candidate.id === updatedAction.id) ?? updatedAction;
        updateNode(validatedAction.id, validatedAction);
    };

    if (!selectedOption || multiple) {
        return (
            <ComposerPanel>
                <ComposerEmptyState icon={<OptionIcon />}>{T._('Select one option to see what it does.', 'Admin')}</ComposerEmptyState>
            </ComposerPanel>
        );
    }

    const nodes = [...filteredData].sort((left, right) => sortByDisplay(filteredData, left, right));
    const OPTION_FACES: ComposerOptionFace[] = ['actions', 'settings', 'links', 'retarget', 'prices', 'properties'];
    const activeTab = Math.max(0, OPTION_FACES.indexOf(optionFace));
    const hasPrice =
        (selectedAttribute.pricings?.some((pricing) => pricing.selectedOptionGuid === selectedOption.id) ?? false) ||
        Boolean(parseComposerPlatformVariantCode(selectedOption.code));
    const priceSupported = [ComposerEcommercePlatform.WooCommerce, ComposerEcommercePlatform.Shopify, ComposerEcommercePlatform.BigCommerce].includes(
        ecommerceSettings?.ecommercePlatform ?? ComposerEcommercePlatform.None
    );
    const updateSelectedOption = (patch: Partial<ComposerAttributeOptionNode>) => {
        const updatedOption = { ...selectedOption, ...patch };
        updateOption(updatedOption);
        if (
            patch.name !== undefined ||
            patch.code !== undefined ||
            patch.links !== undefined ||
            patch.linkOperation !== undefined ||
            Object.prototype.hasOwnProperty.call(patch, 'platformAttribute')
        ) {
            void validateOptions({
                ...selectedAttribute,
                options: selectedAttribute.options.map((option) => (option.id === updatedOption.id ? updatedOption : option))
            });
        }
    };
    const updateSelectedOptionImage = async (uploadedImageUrl: string, tempImageFileKey?: string) => {
        const image = createComposerOptionImageReference(uploadedImageUrl, tempImageFileKey);
        updateOption(applyComposerOptionImage(selectedOption, image));
        return image?.imageUrl ?? '';
    };
    // A local option priced on its own is exempt from the code requirement, so its empty field must not read as broken.
    const hasCustomPricing = (selectedAttribute.pricings || []).some((pricing) => pricing.selectedOptionGuid === selectedOption.id);
    const isStoreConnected = Boolean(selectedAttribute.platformAttribute?.id);
    const isStoreOption = Boolean(selectedOption.platformAttribute?.id) || isComposerStoreCode(selectedOption.code);
    const storeOptionValues = isStoreConnected
        ? (merchantProductOptions.find((attribute) => attribute.optionID === selectedAttribute.platformAttribute?.id)?.values ?? [])
        : [];
    const storeOptions = [
        { value: '', label: T._('Select a store option', 'Admin') },
        ...storeOptionValues.map((option) => ({ value: option.valueID, label: option.label || option.valueID }))
    ];
    const optionNameHasError = hasComposerValidationError(
        selectedOption,
        ComposerValidationErrorCode.OPTION_NAME_REQUIRED,
        ComposerValidationErrorCode.OPTION_NAME_NOT_UNIQUE
    );
    const optionCodeHasError = hasComposerValidationError(
        selectedOption,
        ComposerValidationErrorCode.OPTION_CODE_REQUIRED,
        ComposerValidationErrorCode.OPTION_CODE_NOT_UNIQUE,
        ComposerValidationErrorCode.STORE_CODE_INVALID
    );
    const optionStoreHasError = hasComposerValidationError(
        selectedOption,
        ComposerValidationErrorCode.OPTION_NOT_FROM_PLATFORM,
        ComposerValidationErrorCode.OPTION_FROM_PLATFORM_WITH_LOCAL_ATTRIBUTE,
        ComposerValidationErrorCode.STORE_OPTION_NOT_FOUND,
        ComposerValidationErrorCode.STORE_OPTION_NOT_UNIQUE,
        ComposerValidationErrorCode.STORE_CODE_INVALID
    );

    return (
        <ComposerPanel aria-label={T._('Option details', 'Admin')}>
            <ComposerSubjectHeader>
                <ComposerSubjectMark $kind='option'>{selectedOption.img ? <img src={selectedOption.img} alt='' /> : <OptionIcon />}</ComposerSubjectMark>
                <ComposerSubjectContent>
                    <ComposerSubjectNameInput
                        aria-label={T._('Option name', 'Admin')}
                        placeholder={T._('Enter option name', 'Admin')}
                        isError={optionNameHasError}
                        value={selectedOption.name}
                        onChange={(event) => updateSelectedOption({ name: event.target.value })}
                    />
                    <ComposerSubjectMeta>
                        <ComposerKindBadge $kind='option'>{T._('Option', 'Admin')}</ComposerKindBadge>
                        {isStoreOption ? (
                            <ComposerKindBadge $kind='attribute'>{T._('Store attribute', 'Admin')}</ComposerKindBadge>
                        ) : (
                            <span>{selectedOption.code}</span>
                        )}
                    </ComposerSubjectMeta>
                </ComposerSubjectContent>
                <ComposerRevealButton id={selectedOption.id} />
            </ComposerSubjectHeader>
            <ComposerTabs selectedIndex={activeTab} onSelect={(index) => setOptionFace(OPTION_FACES[index] ?? 'actions')}>
                <TabList hasOverflow>
                    <Tab>
                        {T._('Actions', 'Admin')} <ComposerTabCount>{actions.length}</ComposerTabCount>
                    </Tab>
                    <Tab>{T._('Settings', 'Admin')}</Tab>
                    <Tab>
                        {T._('Links', 'Admin')} <ComposerTabCount>{selectedOption.links.length}</ComposerTabCount>
                    </Tab>
                    <Tab>{T._('Retarget', 'Admin')}</Tab>
                    <Tab>
                        {T._('Prices', 'Admin')} <ComposerTabCount>{hasPrice ? 1 : 0}</ComposerTabCount>
                    </Tab>
                    <Tab>
                        {T._('Properties', 'Admin')} <ComposerTabCount>{selectedOption.extensionFields.length}</ComposerTabCount>
                    </Tab>
                </TabList>
                <TabPanel>
                    <ComposerToolbar>
                        <ComposerAddButton disabled={drafting} onClick={addAction}>
                            <PlusIcon />
                            {T._('Add', 'Admin')}
                        </ComposerAddButton>
                        <ComposerToolbarSpacer />
                        <ZTooltip
                            title={singleCard ? T._('One card at a time: opening a card closes the others', 'Admin') : T._('One card at a time', 'Admin')}
                        >
                            <ComposerIconButton
                                aria-label={T._('One card at a time', 'Admin')}
                                aria-pressed={singleCard}
                                $active={singleCard}
                                onClick={() => {
                                    if (!singleCard) setOpenIds([]);
                                    setSingleCard(!singleCard);
                                }}
                            >
                                <SingleBranchIcon />
                            </ComposerIconButton>
                        </ZTooltip>
                        <ZTooltip
                            title={
                                singleCard && openIds.length === 0
                                    ? T._('One card at a time is on: open a single card from its head', 'Admin')
                                    : openIds.length > 0
                                      ? T._('Collapse all', 'Admin')
                                      : T._('Expand all', 'Admin')
                            }
                        >
                            <ComposerIconButton
                                aria-label={openIds.length > 0 ? T._('Collapse all', 'Admin') : T._('Expand all', 'Admin')}
                                disabled={nodes.length === 0 || (singleCard && openIds.length === 0)}
                                onClick={() => setOpenIds(openIds.length > 0 ? [] : nodes.map((action) => action.id))}
                            >
                                {openIds.length > 0 ? <CollapseAllIcon /> : <ExpandAllIcon />}
                            </ComposerIconButton>
                        </ZTooltip>
                        <ZTooltip title={T._('Run all actions', 'Admin')}>
                            <ComposerIconButton
                                aria-label={T._('Run all actions', 'Admin')}
                                disabled={actions.length === 0}
                                onClick={() => nodes.forEach(playAction)}
                            >
                                <PlayIcon />
                            </ComposerIconButton>
                        </ZTooltip>
                        {selectedIds.length > 0 && (
                            <ZTooltip title={T._('Remove selected actions', 'Admin')}>
                                <ComposerIconButton
                                    $danger
                                    aria-label={T._('Remove selected actions', 'Admin')}
                                    onClick={() => removeActions(selectedIds.map((id) => findNodeById(id)).filter(Boolean) as ComposerOptionActionNode[])}
                                >
                                    <DeleteIcon />
                                </ComposerIconButton>
                            </ZTooltip>
                        )}
                    </ComposerToolbar>
                    <ComposerScrollBody>
                        {/* «ADD» STA IN ALTO, NELLA BARRA DEI COMANDI DELLA LISTA (utente, 2026-10-15): il tratteggiato in
                            fondo faceva la stessa cosa, una seconda porta per la stessa stanza. Resta solo a lista vuota, dove
                            è il posto della prima azione. */}
                        {nodes.length === 0 && !drafting ? (
                            <ComposerEmptyList>
                                <ComposerAddCard type='button' onClick={addAction}>
                                    <PlusIcon />
                                    {T._('Add an action', 'Admin')}
                                </ComposerAddCard>
                                <ComposerFieldNote>
                                    {T._('No actions yet. Add the first one to make this option change the 3D scene.', 'Admin')}
                                </ComposerFieldNote>
                            </ComposerEmptyList>
                        ) : (
                            <ComposerList
                                data-reorder-scope=''
                                role='list'
                                aria-multiselectable='true'
                                onKeyDown={(event) => {
                                    // Esc toglie la scelta, non chiude le card: per chiuderle c'è la loro testata.
                                    if (event.key !== 'Escape' || selectedIds.length === 0) return;
                                    event.stopPropagation();
                                    clearSelection();
                                }}
                            >
                                {nodes.map((action) => (
                                    <ComposerActionTreeElement
                                        key={action.id}
                                        action={action}
                                        selected={selectedIds.includes(action.id)}
                                        findMesh={(id) => meshes.find((mesh) => mesh.id === id || mesh.name === id)}
                                        findMaterial={(id) => materials.find((material) => material.id === id || material.name === id)}
                                        findCamera={(id) => cameras.find((camera) => camera.id === id || camera.name === id)}
                                        findPrintingMethod={(id) => {
                                            const method = printTypes.find((candidate) => String(candidate.printTypeID) === id);
                                            return method ? { name: method.name || id } : undefined;
                                        }}
                                        onDoubleClick={(_, selectedAction) => {
                                            setSelectedIds([selectedAction.id]);
                                            selectActions([selectedAction]);
                                            openCard(selectedAction.id);
                                            setOptionFace('actions');
                                        }}
                                        onGripPointerDown={startActionDrag}
                                        onMoveDown={() => moveAction(action, 'down')}
                                        onMoveUp={() => moveAction(action, 'up')}
                                        onPlay={playAction}
                                        onRemove={(selectedAction) => removeActions([selectedAction])}
                                        onSelect={selectAction}
                                        onVisibilityClick={toggleActionEnabled}
                                        editor={
                                            openIds.includes(action.id) ? (
                                                <ComposerActionEditor
                                                    key={action.id}
                                                    action={action}
                                                    materials={materials}
                                                    meshes={meshes}
                                                    cameras={cameras}
                                                    onChange={changeAction}
                                                />
                                            ) : undefined
                                        }
                                    />
                                ))}
                                {drafting && <ComposerActionDraftCard onChoose={createAction} onDiscard={() => setDrafting(false)} />}
                            </ComposerList>
                        )}
                    </ComposerScrollBody>
                </TabPanel>
                <TabPanel>
                    <ComposerSettingsBody>
                        <ComposerFormStack>
                            <ComposerSection id='option.general' forceOpen={optionCodeHasError || optionStoreHasError} title={T._('General', 'Admin')}>
                                <FormControl label={T._('Code', 'Admin')}>
                                    {isStoreConnected ? (
                                        <ComposerDetailValue>
                                            {selectedOption.platformAttribute
                                                ? selectedOption.platformAttribute.label || selectedOption.platformAttribute.id
                                                : T._('Choose one of the connected store options below.', 'Admin')}
                                        </ComposerDetailValue>
                                    ) : (
                                        <Input
                                            placeholder={T._('Enter option code', 'Admin')}
                                            isError={optionCodeHasError && !hasCustomPricing}
                                            value={selectedOption.code}
                                            onChange={(event) => updateSelectedOption({ code: event.target.value })}
                                        />
                                    )}
                                </FormControl>
                                <FormControl label={T._('Description', 'Admin')}>
                                    <TextArea
                                        rows={4}
                                        placeholder={T._('Enter option description', 'Admin')}
                                        value={selectedOption.description}
                                        onChange={(event) => updateSelectedOption({ description: event.target.value })}
                                    />
                                </FormControl>
                                {isStoreConnected && (
                                    <FormControl label={T._('Store option', 'Admin')}>
                                        <ComposerSlot
                                            id={`composer-option-${selectedOption.id}-store`}
                                            value={selectedOption.platformAttribute?.id || undefined}
                                            options={storeOptions.filter((option) => option.value)}
                                            glyph={<StoreIcon />}
                                            caption={T._('Store option', 'Admin')}
                                            emptyLabel={T._('Not connected', 'Admin')}
                                            emptyCaption={T._('Choose a store option', 'Admin')}
                                            searchPlaceholder={T._('Search store options…', 'Admin')}
                                            noOptionsLabel={T._(
                                                'Store options are not available. Create them in the store before connecting this option.',
                                                'Admin'
                                            )}
                                            missingLabel={selectedOption.platformAttribute?.label}
                                            isError={optionStoreHasError}
                                            clearable
                                            onChange={(value) => {
                                                const selection = storeOptions.find((option) => option.value === value && value);
                                                const platformAttribute = selection ? { id: selection.value, label: selection.label } : undefined;
                                                updateSelectedOption({
                                                    platformAttribute,
                                                    code: platformAttribute
                                                        ? JSON.stringify({ ...platformAttribute, selected: true })
                                                        : createComposerLocalCode(selectedOption.name, 'option')
                                                });
                                            }}
                                        />
                                    </FormControl>
                                )}
                            </ComposerSection>
                            <ComposerSection id='option.preview-image' title={T._('Preview image', 'Admin')}>
                                <ComposerUploadComponent passedImgUrl={selectedOption.img} onImagePick={updateSelectedOptionImage} />
                                <Composer3DPreviewCapture
                                    materials={materials}
                                    onImagePick={updateSelectedOptionImage}
                                    successMessage={T._('The 3D preview was assigned to this option.', 'Admin')}
                                />
                            </ComposerSection>
                        </ComposerFormStack>
                    </ComposerSettingsBody>
                </TabPanel>
                <TabPanel>
                    <ComposerLinksEditor
                        attributes={attributes}
                        links={selectedOption.links}
                        logicalOperator={selectedOption.linkOperation}
                        selfAttributeID={selectedAttribute.id}
                        subjectLabel={T._('This option', 'Admin')}
                        validationErrors={selectedOption.brokenValues}
                        onChange={(links, linkOperation) => updateSelectedOption({ links, linkOperation })}
                    />
                </TabPanel>
                <TabPanel>
                    <ComposerRetargetPanel
                        actions={actions}
                        objects={meshes}
                        materials={materials}
                        subjectLabel={selectedOption.name}
                        onApply={(remapped) => {
                            remapped.forEach((action) => updateNode(action.id, action));
                            updateSelectedOption({ actions: remapped });
                        }}
                    />
                </TabPanel>
                <TabPanel>
                    <TabBody>
                        {/* Anche il prezzo è una sezione della scena, come le Settings (utente, 2026-10-15). */}
                        <ComposerSection id='option.price' title={T._('Price', 'Admin')}>
                            {priceSupported ? (
                                <PriceContent
                                    selectedAttribute={selectedAttribute}
                                    selectedOption={selectedOption}
                                    ecommercePlatform={ecommerceSettings?.ecommercePlatform ?? ComposerEcommercePlatform.None}
                                    onUpdateAttribute={updateAttribute}
                                    onUpdateOption={updateSelectedOption}
                                />
                            ) : (
                                <ComposerUnsupportedNote>
                                    {T._('Option pricing is available for WooCommerce, Shopify, and BigCommerce.', 'Admin')}
                                </ComposerUnsupportedNote>
                            )}
                        </ComposerSection>
                    </TabBody>
                </TabPanel>
                <TabPanel>
                    <TabBody>
                        <ComposerSection id='option.properties' title={T._('Properties', 'Admin')}>
                            <PropertiesContent selectedOption={selectedOption} onUpdate={updateOption} />
                        </ComposerSection>
                    </TabBody>
                </TabPanel>
            </ComposerTabs>
        </ComposerPanel>
    );
};

export default ComposerActions;
